import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { getRecentProducts } from '../services/productsService';
import { ShoppingCart, ArrowRight } from 'phosphor-react';

// Helper function to check if a product is new (released within 30 days)
const isNewProduct = (releaseDate) => {
  if (!releaseDate) return false;
  const release = new Date(releaseDate);
  const now = new Date();
  const diffTime = Math.abs(now - release);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays <= 30;
};

function Home() {
  const { t } = useTranslation();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch featured products
  useEffect(() => {
    const fetchRecentProducts = async () => {
      try {
        const products = await getRecentProducts();
        setFeaturedProducts(products);
      } catch (error) {
        console.error('Error fetching recent products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentProducts();
  }, []);

  const handleAddToCart = (product) => {
    const cartItem = {
      id: product.id,
      name: product.name,
      price: product.price,
      quantity: 1,
      category_id: product.category_id,
      image_url: product.image_url,
      use_standard_pricing: product.use_standard_pricing
    };
    addToCart(cartItem);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Hero Section with integrated navbar */}
      <section className="relative bg-amber-50 h-[120vh] lg:h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center px-8">
          <div className="max-w-6xl mx-auto">
            {/* Mobile layout - stacked vertically */}
            <div className="flex flex-col items-center text-center lg:hidden space-y-8">
              <h1 className="text-3xl font-bold text-amber-800 leading-tight">
                Wenskaartjes die doen<br />
                glimlachen, troosten en<br />
                raken
              </h1>
              
              <div className="w-64 h-64 rounded-full overflow-hidden shadow-2xl">
                <img 
                  src="/assets/images/atelier-ambree-round.png" 
                  alt="Atelier Ambree collage" 
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* <p className="text-normal text-amber-700">
                Ontdek alle kaartjes in de shop
              </p> */}
              
              <Link 
                to="/shop" 
                className="inline-flex items-center space-x-2 bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg text-md font-semibold transition-colors"
              >
                <span>Shop nu</span>
                <img 
                  src="/assets/images/mail-fast.svg" 
                  alt="Mail icon" 
                  className="h-10 w-10 filter brightness-0 invert"
                />
              </Link>
            </div>
            
            {/* Desktop layout - side by side */}
            <div className="hidden lg:grid grid-cols-2 gap-12 items-center">
              {/* Left side - Text content */}
              <div className="space-y-6">
                <h1 className="text-4xl md:text-5xl lg:text-5xl font-bold text-amber-800 leading-tight py-8">
                  Wenskaartjes die doen<br />
                  glimlachen, troosten en<br />
                  raken
                </h1>
                <p className="text-xl text-amber-700 mb-6">
                  Ontdek alle kaartjes in de shop
                </p>
                <Link 
                  to="/shop" 
                  className="inline-flex items-center space-x-2 bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors"
                >
                  <span>Shop nu</span>
                  <img 
                    src="/assets/images/mail-fast.svg" 
                    alt="Mail icon" 
                    className="h-10 w-10 filter brightness-0 invert"
                  />
                </Link>
              </div>
              
              {/* Right side - Circular image */}
              <div className="flex justify-center lg:justify-end">
                <div className="w-80 h-80 lg:w-[32rem] lg:h-[32rem] rounded-full overflow-hidden shadow-2xl">
                  <img 
                    src="/assets/images/atelier-ambree-round.png" 
                    alt="Atelier Ambree collage" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Arrow with text at bottom of hero */}
        <div className="flex flex-col items-center pb-8">
          <p className="text-amber-700 text-lg font-medium mb-4">
            Ontdek de nieuwste kaartjes
          </p>
          <div className="animate-bounce">
            <svg 
              className="w-6 h-6 text-amber-600" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M19 14l-7 7m0 0l-7-7m7 7V3" 
              />
            </svg>
          </div>
        </div>
      </section>
      
      {/* Featured Products */}
      <section className="py-20 px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-end items-center mb-12">
            {/* <h2 className="text-2xl font-semibold text-gray-900">{t('home.featured.title')}</h2> */}
            <Link to="/shop" className="text-ambre-600 font-bold hover:text-ambre-700 transition flex items-center space-x-2">
              <span>{t('home.featured.viewAllCards')}</span>
              <ArrowRight size={16} />
            </Link>
          </div>
          
          {loading ? (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ambre-600"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-10 lg:grid-cols-3 lg:gap-x-8">
              {featuredProducts.map((product) => (
                <Link key={product.id} to={`/product/${product.id}`} className="group relative">
                  <div className="w-full bg-gray-200 overflow-hidden group-hover:opacity-75 transition-opacity shadow-lg relative" style={{ aspectRatio: '105/148' }}>
                    {isNewProduct(product.release_date) && (
                      <div className="absolute top-2 left-2 z-10">
                        <span className="bg-green-600 text-white text-xs font-medium px-2 py-1 rounded-full shadow-md">
                          {t('product.new')}
                        </span>
                      </div>
                    )}
                    {product.stock_quantity === 0 && (
                      <div className="absolute top-2 right-2 z-10">
                        <span className="bg-red-500 text-white text-xs font-medium px-2 py-1 rounded-full shadow-md">
                          {t('product.temporarilyOutOfStock')}
                        </span>
                      </div>
                    )}
                    {product.image_url ? (
                      <img 
                        src={product.image_url} 
                        alt={product.name}
                        className="w-full h-full object-center object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="w-full h-full bg-gradient-to-br from-ambre-100 to-ambre-200 flex items-center justify-center">
                      <span className="text-lg font-bold text-ambre-600">{product.category?.name || 'No Category'}</span>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 flex-1">
                        <span aria-hidden="true" className="absolute inset-0" />
                        {product.name}
                      </h3>
                      <span className="ml-3 px-2 py-1 text-xs font-medium bg-ambre-100 text-ambre-800 rounded-full">
                        {product.category?.name || 'No Category'}
                      </span>
                    </div>
                    <p className="text-lg font-bold text-ambre-600">€{product.price.toFixed(2)}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      
      <Footer />
    </div>
  );
}

export default Home; 